import { setting } from '@/lib/settings';
import { type AnalyticsEvent, type EventKind } from '@shared/analytics';

/**
 * First-party analytics. No third-party script, no persistent identifier, no personal data.
 *
 * The queue flushes on a timer, at a batch size, and on the two events that mean "this page is
 * about to stop existing", using sendBeacon so the last batch survives a navigation.
 */

const SESSION_KEY = 'ee_session_id';

/**
 * A v4 UUID. `crypto.randomUUID` is the obvious call, and it is also the one that is absent on
 * an insecure origin — a phone pointed at `just dev` over the LAN — where it used to throw at
 * import time and leave the whole bundle unmounted. The fallback is the same thing by hand.
 */
function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Held in memory as well as in sessionStorage. Storage throws where site data is blocked —
 * Safari with all cookies refused, Firefox with the site's data blocked — and an analytics
 * beacon is never a reason to let a tap fail, so a tab that cannot store one simply keeps its
 * id for as long as the page lives.
 */
let session: string | null = null;

function sessionId(): string {
  if (session) return session;
  try {
    session = sessionStorage.getItem(SESSION_KEY);
  } catch {
    session = null;
  }
  if (!session) {
    session = uuid();
    try {
      sessionStorage.setItem(SESSION_KEY, session);
    } catch {
      // Kept in memory alone.
    }
  }
  return session;
}

let queue: AnalyticsEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let seq = 0;
let viewId = uuid();

export function startView(): void {
  viewId = uuid();
}

function send(payload: AnalyticsEvent[]): void {
  const body = JSON.stringify({ events: payload });
  // sendBeacon survives the page going away; fetch is the fallback when it is unavailable.
  try {
    if (navigator.sendBeacon?.('/api/e', new Blob([body], { type: 'application/json' }))) return;
  } catch {
    // Some browsers throw rather than answer false for a refused beacon; fall through.
  }
  void fetch('/api/e', { method: 'POST', body, keepalive: true, headers: { 'content-type': 'application/json' } }).catch(
    () => undefined,
  );
}

export function flush(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!queue.length) return;
  const batch = queue;
  queue = [];
  send(batch);
}

export function track(
  kind: EventKind,
  name: string,
  detail: Partial<AnalyticsEvent> = {},
): void {
  seq += 1;
  queue.push({
    kind,
    name,
    session: sessionId(),
    view: viewId,
    seq,
    route: window.location.pathname,
    viewport: window.innerWidth,
    dpr: window.devicePixelRatio,
    ...detail,
  });

  if (queue.length >= setting('analytics.batch_size')) {
    flush();
    return;
  }
  timer ??= setTimeout(flush, setting('analytics.flush_interval_ms'));
}

/** Dwell under the floor is discarded: a card that merely scrolled past says nothing. */
export function trackDwell(ms: number, detail: Partial<AnalyticsEvent>): void {
  if (ms < setting('analytics.dwell_floor_ms')) return;
  track('impression', detail.photo ? 'impression.photo' : 'impression.card', {
    ...detail,
    value: Math.round(ms),
  });
}

/** Reads the instrumentation contract off the DOM: data-ee-feature and its ancestors. */
export function describeTarget(target: Element | null): Partial<AnalyticsEvent> & { name?: string } {
  const element = target?.closest?.('[data-ee-feature]') as HTMLElement | null;
  if (!element) return {};
  const context = element.closest('[data-ee-advertiser]') as HTMLElement | null;
  return {
    name: element.dataset.eeFeature,
    advertiser: element.dataset.eeAdvertiser ?? context?.dataset.eeAdvertiser,
    listing: element.dataset.eeListing ?? context?.dataset.eeListing,
    photo: element.dataset.eePhoto ?? context?.dataset.eePhoto,
  };
}
